import React, { useMemo, useState } from "react";
import styles from "./CommandModals.module.scss";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { Checkbox } from "@whatslly/ui-components/v2/Checkbox";
import { IModalInstance } from "@whatslly/ui-components/modals/ModalProvider";
import { BaseModal } from "@whatslly/ui-components/modals";
import type { SidecarCommandRequest } from "@whatslly/types/sidecars-server/SidecarCommands";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import type { SelectOption } from "@whatslly/types/UI-V2/BaseElements/SelectType";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { useDependencyInjector } from "@whatslly/commons/di";

type optionalCommands = SidecarCommandRequest | { command: "startSidecar"; verbose?: boolean; resyncAllMessages?: boolean };

type SpecificCommandProps<T extends optionalCommands["command"]> = {
  cmd: optionalCommands & { command: T };
  setCmd: (cmd: optionalCommands & { command: T }) => void;
};

export const KillSettings: React.FC<SpecificCommandProps<"kill">> = ({ cmd, setCmd }) => {
  return (
    <label className={styles.checkboxWrapper}>
      <Checkbox
        size="large"
        testId="graceful-checkbox"
        classNameContainer={styles.checkbox}
        value={cmd.graceful}
        onChange={(e) => setCmd({ ...cmd, graceful: e.target.checked })}
      />
      <span>Graceful</span>
    </label>
  );
};

export const SelectCommandModal: React.FC<IModalInstance<optionalCommands>> = ({ closeModal }) => {
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const options = useMemo(() => {
    const defaultOptions: SelectOption<{ value: optionalCommands }>[] = [
      { label: "Take screenshot", value: { command: "screenshot" as const } },
      { label: "Start Verbose", value: { command: "startSidecar" as const, verbose: true } },
      { label: "Keep alive", value: { command: "forceKeepAlive" as const } },
      { label: "Kill", value: { command: "kill" as const, graceful: true } },
    ];
    if (userStore.isSuperAdmin) {
      defaultOptions.push({ label: "Resync All Messages", value: { command: "startSidecar" as const, resyncAllMessages: true } });
    }
    return defaultOptions;
  }, [userStore.isSuperAdmin]);

  const [selectedCommand, setSelectedCommand] = useState<(typeof options)[number]>();
  return (
    <BaseModal closeModal={closeModal} className={styles.selectCommandModal}>
      <Select<SelectOption<{ value: optionalCommands }>>
        paddingInline={"none"}
        name={"calendarType"}
        data-testid="select-command"
        value={selectedCommand}
        onChange={setSelectedCommand}
        options={options}
      />
      {selectedCommand?.value.command === "kill" && (
        <KillSettings cmd={selectedCommand.value} setCmd={(cmd) => setSelectedCommand({ ...selectedCommand, value: cmd })} />
      )}
      <ModalButtons onSubmit={() => closeModal(selectedCommand?.value)} hideCancel={true} testId="submit-command-modal" />
    </BaseModal>
  );
};
